import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Converts markdown text to HTML string for clipboard operations
 */
export const convertMarkdownToHtml = (markdown: string): string => {
  const markdownComponent = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h1>{children}</h1>,
        h2: ({ children }) => <h2>{children}</h2>,
        h3: ({ children }) => <h3>{children}</h3>,
        h4: ({ children }) => <h4>{children}</h4>,
        h5: ({ children }) => <h5>{children}</h5>,
        h6: ({ children }) => <h6>{children}</h6>,
        p: ({ children }) => <p>{children}</p>,
        strong: ({ children }) => <strong>{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        ul: ({ children }) => <ul>{children}</ul>,
        ol: ({ children }) => <ol>{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
        blockquote: ({ children }) => <blockquote>{children}</blockquote>,
        code: ({ children, inline }) =>
          inline ? (
            <code>{children}</code>
          ) : (
            <pre>
              <code>{children}</code>
            </pre>
          ),
        table: ({ children }) => (
          <table border={1} cellPadding={8} cellSpacing={0}>
            {children}
          </table>
        ),
        thead: ({ children }) => <thead>{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr>{children}</tr>,
        th: ({ children }) => <th>{children}</th>,
        td: ({ children }) => <td>{children}</td>,
        hr: () => <hr />,
        br: () => <br />,
      }}
    >
      {markdown}
    </ReactMarkdown>
  );

  const htmlString = renderToStaticMarkup(markdownComponent);

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333;">
      ${htmlString}
    </div>
  `.trim();
};

/**
 * Copies both HTML and plain text to clipboard for maximum compatibility
 */
export const copyMarkdownAsFormattedText = async (markdown: string): Promise<boolean> => {
  try {
    const html = convertMarkdownToHtml(markdown);

    const clipboardItem = new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([markdown], { type: "text/plain" }),
    });

    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (error) {
    try {
      await navigator.clipboard.writeText(markdown);
      return true;
    } catch (err) {
      return false;
    }
  }
};
